import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Home, Download, Sparkles } from 'lucide-react';
import { useAuth } from './AuthContext';
import { getPrimaryRole } from '../utils/permissions';
import { assistantAPI } from '../api/apiService';
import './HRAssistantChat.css';

/* ── Format helpers ────────────────────────────────────────────────────── */

// Humanize snake_case / camelCase column names into "Title Case".
const humanizeHeader = (s) => {
  if (!s) return s;
  return String(s)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Guess if a value looks numeric (for right-alignment).
const isNumericCell = (v) => {
  if (v == null || v === '') return false;
  const s = String(v).replace(/[,₹$%\s]/g, '');
  return s !== '' && !isNaN(Number(s));
};

// Render an answer string as prose + markdown pipe tables.
// Recognises:
//   - "Found N record(s):" count header (rendered as a styled badge)
//   - Pipe tables: "| col | col |" header + "| --- |" separator + data rows
const renderAnswerWithTables = (text) => {
  if (!text || typeof text !== 'string') return text;
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  let buffer = [];
  const flushBuffer = () => {
    if (buffer.length) {
      const joined = buffer.join('\n').trim();
      if (joined) blocks.push({ type: 'text', content: joined });
      buffer = [];
    }
  };
  const isPipeRow = (l) => /^\s*\|.*\|\s*$/.test(l);
  const isSeparator = (l) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(l);
  const splitCells = (l) =>
    l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

  const foundRe = /^Found\s+(\d+)\s+record\(s\):?\s*$/i;

  while (i < lines.length) {
    const line = lines[i];
    const foundMatch = line.match(foundRe);
    if (foundMatch) {
      flushBuffer();
      blocks.push({ type: 'count', count: parseInt(foundMatch[1], 10) });
      i += 1;
      continue;
    }
    if (isPipeRow(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      flushBuffer();
      const header = splitCells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && isPipeRow(lines[i]) && !isSeparator(lines[i])) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    buffer.push(line);
    i += 1;
  }
  flushBuffer();

  // Merge an adjacent count + table into a single result card.
  const merged = [];
  for (let k = 0; k < blocks.length; k += 1) {
    if (
      blocks[k].type === 'count' &&
      k + 1 < blocks.length &&
      blocks[k + 1].type === 'table'
    ) {
      merged.push({ ...blocks[k + 1], count: blocks[k].count });
      k += 1;
    } else {
      merged.push(blocks[k]);
    }
  }

  if (merged.length === 0) {
    return <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>;
  }

  return merged.map((b, idx) => {
    if (b.type === 'table') {
      const numericCols = b.header.map((_, colIdx) => {
        const sample = b.rows.slice(0, 5).map((r) => r[colIdx]);
        return sample.length > 0 && sample.every((v) => v === '' || isNumericCell(v));
      });
      return (
        <div key={idx} className="hra-result-card">
          <div className="hra-result-header">
            <span className="hra-result-title">
              <span className="hra-result-dot" />
              Results
            </span>
            {b.count != null && (
              <span className="hra-result-count">
                {b.count} {b.count === 1 ? 'record' : 'records'}
              </span>
            )}
          </div>
          <div className="hra-table-wrap">
            <table className="hra-data-table">
              <thead>
                <tr>
                  {b.header.map((h, j) => (
                    <th
                      key={j}
                      className={numericCols[j] ? 'is-numeric' : undefined}
                    >
                      {humanizeHeader(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, ri) => (
                  <tr key={ri}>
                    {r.map((c, ci) => (
                      <td
                        key={ci}
                        className={numericCols[ci] ? 'is-numeric' : undefined}
                        title={c}
                      >
                        {c === '' ? <span className="hra-cell-empty">—</span> : c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (b.type === 'count') {
      return (
        <div key={idx} className="hra-result-count-standalone">
          <span className="hra-result-dot" />
          {b.count} {b.count === 1 ? 'record' : 'records'}
        </div>
      );
    }
    return (
      <div key={idx} style={{ whiteSpace: 'pre-wrap' }}>
        {b.content}
      </div>
    );
  });
};

const fmt = (n) => {
  if (n == null || isNaN(n)) return '0';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtRupee = (n) => `\u20B9${fmt(n)}`;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

/* ── Role-based menu definitions ───────────────────────────────────────── */

const EMPLOYEE_MENU = [
  { id: 'view_payslip',    icon: '\uD83D\uDCB0', label: 'View Payslip',       color: 'hra-icon-indigo' },
  { id: 'apply_leave',     icon: '\uD83C\uDFD6\uFE0F', label: 'Apply Leave',        color: 'hra-icon-emerald' },
  { id: 'leave_balance',   icon: '\uD83D\uDCCA', label: 'Leave Balance',      color: 'hra-icon-blue' },
  { id: 'form16',          icon: '\uD83D\uDCC4', label: 'Download Form 16',   color: 'hra-icon-amber' },
  { id: 'my_profile',      icon: '\uD83D\uDC64', label: 'My Profile',         color: 'hra-icon-purple' },
];

const MANAGER_MENU = [
  { id: 'approve_leave',   icon: '\uD83C\uDFD6\uFE0F', label: 'Approve Leave',      color: 'hra-icon-emerald' },
  { id: 'team_attendance', icon: '\uD83D\uDC65', label: 'Team Attendance',    color: 'hra-icon-blue' },
  { id: 'alerts',          icon: '\u26A0\uFE0F', label: 'Alerts',             color: 'hra-icon-amber' },
];

const CLIENT_ADMIN_MENU = [
  { id: 'payroll_overview', icon: '\uD83D\uDCB0', label: 'Payroll Overview',      color: 'hra-icon-indigo'  },
  { id: 'run_payroll',      icon: '\u25B6\uFE0F', label: 'Run Payroll',           color: 'hra-icon-emerald' },
  { id: 'reports',          icon: '\uD83D\uDCCA', label: 'Reports & Analytics',   color: 'hra-icon-blue'    },
  { id: 'compliance',       icon: '\uD83D\uDCC4', label: 'Compliance & Statutory Forms', color: 'hra-icon-amber' },
  { id: 'employees',        icon: '\uD83D\uDC65', label: 'Employee Summary',      color: 'hra-icon-purple'  },
  { id: 'org_settings',     icon: '\u2699\uFE0F', label: 'Organization Settings', color: 'hra-icon-teal'    },
];

const SUPER_ADMIN_MENU = [
  { id: 'payroll_overview', icon: '\uD83D\uDCB0', label: 'Payroll Overview',             color: 'hra-icon-indigo'  },
  { id: 'reports',          icon: '\uD83D\uDCCA', label: 'Reports & Analytics',          color: 'hra-icon-blue'    },
  { id: 'compliance',       icon: '\uD83D\uDCC4', label: 'Compliance & Statutory Forms', color: 'hra-icon-amber'   },
  { id: 'employees',        icon: '\uD83D\uDC65', label: 'Employee Summary',             color: 'hra-icon-purple'  },
  { id: 'org_settings',     icon: '\u2699\uFE0F', label: 'Organization Settings',        color: 'hra-icon-teal'    },
];

const HR_MENU = [
  { id: 'run_payroll',     icon: '\uD83D\uDCB0', label: 'Run Payroll',        color: 'hra-icon-indigo' },
  { id: 'payroll_summary', icon: '\uD83D\uDCCA', label: 'Payroll Summary',    color: 'hra-icon-emerald' },
  { id: 'employees',       icon: '\uD83D\uDC65', label: 'Employees',          color: 'hra-icon-blue' },
  { id: 'compliance',      icon: '\uD83D\uDCC4', label: 'Compliance',         color: 'hra-icon-amber' },
  { id: 'alerts',          icon: '\u26A0\uFE0F', label: 'Alerts',             color: 'hra-icon-rose' },
];


/* ── Main Component ────────────────────────────────────────────────────── */

export default function HRAssistantChat() {
  const { user } = useAuth();
  const role = getPrimaryRole(user);
  const scrollRef = useRef(null);

  // Messages: { id, type: 'bot'|'user', content: ReactNode|string }
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFlow, setActiveFlow] = useState(null);
  const [flowState, setFlowState] = useState({});
  const [queryText, setQueryText] = useState('');

  const isEmployee = role === 'Member';
  const isSuperAdmin = role === 'SuperAdmin';
  const isClientAdmin = role === 'ClientAdmin' || role === 'MultiClientAdmin';
  const isManager = isClientAdmin;
  const isHR = isSuperAdmin;
  const userName = user?.username || user?.name || 'there';

  // For SuperAdmin / MultiClientAdmin: which org is the active scope right now
  const selectedOrgRef = useRef(null);
  const orgsCacheRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Build menu based on role
  const getMenu = useCallback(() => {
    if (isEmployee) return EMPLOYEE_MENU;
    if (isSuperAdmin) return SUPER_ADMIN_MENU;
    if (isClientAdmin) return CLIENT_ADMIN_MENU;
    return MANAGER_MENU;
  }, [isEmployee, isSuperAdmin, isClientAdmin]);

  // Add a message
  const addMsg = useCallback((type, content) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), type, content }]);
  }, []);

  // Step history stack — each entry is a render function that reproduces the step
  const stepStackRef = useRef([]);

  const pushStep = useCallback((fn) => {
    stepStackRef.current = [...stepStackRef.current, fn];
    fn();
  }, []);

  // Reset to home
  const goHome = useCallback(() => {
    stepStackRef.current = [];
    selectedOrgRef.current = null;
    setActiveFlow(null);
    setFlowState({});
    setMessages([]);
    setLoading(false);
  }, []);

  const goBack = useCallback(() => {
    const stack = stepStackRef.current;
    if (stack.length <= 1) {
      goHome();
      return;
    }
    const newStack = stack.slice(0, -1);
    stepStackRef.current = newStack;
    setMessages([]);
    setLoading(false);
    const prev = newStack[newStack.length - 1];
    if (typeof prev === 'function') prev();
  }, [goHome]);

  // Initialize welcome on mount
  useEffect(() => {
    setMessages([]);
    setActiveFlow(null);
  }, []); // eslint-disable-line

  /* ── FLOW HANDLERS ──────────────────────────────────────────────────── */

  // 1. VIEW PAYSLIP — ask for month first
  const handleViewPayslip = () => {
    setActiveFlow('view_payslip');
    addMsg('user', 'View Payslip');
    const now = new Date();
    const year = now.getFullYear();
    addMsg('bot', (
      <div>
        <div>Select the month you want to view:</div>
        <div className="hra-month-grid">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              className="hra-month-btn"
              onClick={() => handleViewPayslipMonth(i + 1, year)}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          Showing months for {year}
        </div>
      </div>
    ));
  };

  const handleViewPayslipMonth = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      const res = await assistantAPI.getLatestPayslip(month, year);
      setLoading(false);
      if (!res.payslip) {
        addMsg('bot', (
          <div>
            <div>No payslip found for <strong>{MONTHS[month - 1]} {year}</strong>.</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Payroll may not have been processed yet for this month. Try another month.
            </div>
            <div className="hra-actions" style={{ marginTop: 8 }}>
              <button className="hra-action-btn" onClick={handleViewPayslip}>
                Pick another month
              </button>
            </div>
          </div>
        ));
        return;
      }
      const p = res.payslip;
      addMsg('bot', (
        <div>
          <div>Here is your <strong>{p.month_name} {p.year}</strong> payslip summary:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Gross Salary</span>
              <span className="hra-row-value">{fmtRupee(p.gross_wages)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Deductions</span>
              <span className="hra-row-value">PF {fmtRupee(p.pf_amount)} | TDS {fmtRupee(p.tds)}</span>
            </div>
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Net Salary</span>
              <span className="hra-row-value">{fmtRupee(p.net_payable)}</span>
            </div>
          </div>
          <div className="hra-actions">
            <button className="hra-action-btn" onClick={() => handlePayslipDownload(p.month, p.year)}>
              <Download size={14} /> Download PDF
            </button>
            <button className="hra-action-btn" onClick={() => handlePayslipBreakdown(p.month, p.year)}>
              <span>📊</span> Explain Breakdown
            </button>
            <button className="hra-action-btn" onClick={handlePayslipCompare}>
              <span>📅</span> Compare Last Month
            </button>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch payslip: {err.message}</div>);
    }
  };

  const handlePayslipDownload = async (month, year) => {
    addMsg('user', 'Download PDF');
    setLoading(true);
    try {
      const res = await assistantAPI.getPayslipPdfLink(month, year);
      setLoading(false);
      if (!res.success || !res.download_url) {
        addMsg('bot', (
          <div>
            <div>{res.message || 'Payslip PDF not available for this period.'}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Ask HR to generate Form 19 (Payslip) for {MONTHS[month - 1]} {year}.
            </div>
          </div>
        ));
        return;
      }
      window.open(res.download_url, '_blank');
      addMsg('bot', (
        <div>
          <div>Your payslip PDF is opening in a new tab.</div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
            If the download didn't start,{' '}
            <a href={res.download_url} target="_blank" rel="noreferrer" style={{ color: '#6366f1', fontWeight: 600 }}>
              click here
            </a>.
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch payslip PDF: {err.message}</div>);
    }
  };

  const handlePayslipBreakdown = async (month, year) => {
    addMsg('user', 'Explain Breakdown');
    setLoading(true);
    try {
      const res = await assistantAPI.getPayslipBreakdown(month, year);
      setLoading(false);
      if (!res.breakdown) {
        addMsg('bot', <div>No breakdown data available.</div>);
        return;
      }
      const b = res.breakdown;
      addMsg('bot', (
        <div>
          <div>Here's your salary breakdown for <strong>{b.month_name} {b.year}</strong>:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <h4 style={{ color: '#16a34a' }}>Earnings</h4>
            {b.earnings.map((e, i) => (
              <div className="hra-result-row" key={i}>
                <span className="hra-row-label">{e.label}</span>
                <span className="hra-row-value">{fmtRupee(e.amount)}</span>
              </div>
            ))}
          </div>
          <div className="hra-result-card" style={{ marginTop: 8 }}>
            <h4 style={{ color: '#dc2626' }}>Deductions</h4>
            {b.deductions.map((d, i) => (
              <div className="hra-result-row" key={i}>
                <span className="hra-row-label">{d.label}</span>
                <span className="hra-row-value">{fmtRupee(d.amount)}</span>
              </div>
            ))}
          </div>
          <div className="hra-result-card" style={{ marginTop: 8 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Gross</span>
              <span className="hra-row-value">{fmtRupee(b.gross_wages)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Deductions</span>
              <span className="hra-row-value">{fmtRupee(b.total_deductions)}</span>
            </div>
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Net Payable</span>
              <span className="hra-row-value">{fmtRupee(b.net_payable)}</span>
            </div>
          </div>
          {b.deductions.some(d => d.note) && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
              {b.deductions.filter(d => d.note).map((d, i) => (
                <div key={i}>• {d.label}: {d.note}</div>
              ))}
            </div>
          )}
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch breakdown: {err.message}</div>);
    }
  };

  const handlePayslipCompare = async () => {
    addMsg('user', 'Compare Last Month');
    setLoading(true);
    try {
      const res = await assistantAPI.comparePayslips();
      setLoading(false);
      if (!res.comparison) {
        addMsg('bot', <div>{res.message || 'Need at least 2 months of data to compare.'}</div>);
        return;
      }
      const c = res.comparison;
      const diffSign = (v) => v > 0 ? '+' : v < 0 ? '' : '';
      const diffClass = (v) => v > 0 ? 'hra-diff-up' : v < 0 ? 'hra-diff-down' : 'hra-diff-same';

      addMsg('bot', (
        <div>
          <div>Comparison (<strong>{c.previous.month_name}</strong> vs <strong>{c.current.month_name}</strong>):</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Net Salary</span>
              <span className="hra-row-value">
                {fmtRupee(c.previous.net_payable)} → {fmtRupee(c.current.net_payable)}
                {' '}<span className={diffClass(c.diff.net_payable)}>({diffSign(c.diff.net_payable)}{fmtRupee(Math.abs(c.diff.net_payable))})</span>
              </span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">TDS</span>
              <span className="hra-row-value">
                {fmtRupee(c.previous.tds)} → {fmtRupee(c.current.tds)}
                {c.diff.tds !== 0 && (
                  <span className={diffClass(c.diff.tds)}> ({diffSign(c.diff.tds)}{fmtRupee(Math.abs(c.diff.tds))})</span>
                )}
              </span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">PF</span>
              <span className="hra-row-value">
                {fmtRupee(c.previous.pf_amount)} → {fmtRupee(c.current.pf_amount)}
              </span>
            </div>
            {c.diff.unpaid_leave_days !== 0 && (
              <div className="hra-result-row">
                <span className="hra-row-label">Unpaid Leave</span>
                <span className="hra-row-value hra-diff-down">
                  {c.current.unpaid_leave_days} day(s) detected
                </span>
              </div>
            )}
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to compare: {err.message}</div>);
    }
  };

  // 2. APPLY LEAVE
  const handleApplyLeave = () => {
    setActiveFlow('apply_leave');
    setFlowState({ step: 'type' });
    addMsg('user', 'Apply Leave');
    addMsg('bot', (
      <div>
        <div>Select leave type:</div>
        <div className="hra-option-grid">
          {['Casual', 'Sick', 'Earned'].map(t => (
            <button key={t} className="hra-option-btn" onClick={() => handleLeaveTypeSelect(t)}>
              {t === 'Casual' ? '🏖️' : t === 'Sick' ? '🏥' : '📋'} {t} Leave
            </button>
          ))}
        </div>
      </div>
    ));
  };

  const handleLeaveTypeSelect = (type) => {
    addMsg('user', `${type} Leave`);
    setFlowState({ step: 'dates', leaveType: type.toLowerCase() });
    addMsg('bot', <LeaveeDatePicker onSubmit={(start, end) => handleLeaveDates(type.toLowerCase(), start, end)} />);
  };

  const handleLeaveDates = (type, start, end) => {
    addMsg('user', `${start} to ${end}`);
    setFlowState({ step: 'reason', leaveType: type, startDate: start, endDate: end });
    addMsg('bot', <LeaveReasonInput onSubmit={(reason) => handleLeaveReason(type, start, end, reason)} />);
  };

  const handleLeaveReason = (type, start, end, reason) => {
    if (reason) addMsg('user', reason);
    const days = Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
    addMsg('bot', (
      <div className="hra-confirm-box">
        <p>Apply <strong>{type}</strong> leave from <strong>{start}</strong> to <strong>{end}</strong> ({days} day{days > 1 ? 's' : ''})?</p>
        <div className="hra-confirm-actions">
          <button className="hra-action-btn hra-action-success" onClick={() => submitLeave(type, start, end, reason)}>
            ✅ Submit
          </button>
          <button className="hra-action-btn hra-action-danger" onClick={() => { addMsg('user', 'Cancel'); addMsg('bot', 'Leave application cancelled.'); }}>
            ❌ Cancel
          </button>
        </div>
      </div>
    ));
  };

  const submitLeave = async (type, start, end, reason) => {
    addMsg('user', 'Submit');
    setLoading(true);
    try {
      const res = await assistantAPI.applyLeave({
        leave_type: type,
        start_date: start,
        end_date: end,
        reason: reason || '',
      });
      setLoading(false);
      addMsg('bot', (
        <div>
          <div className="hra-alert hra-alert-success">
            <span className="hra-alert-icon">✅</span>
            <div className="hra-alert-body">
              <div className="hra-alert-title">Leave Request Submitted</div>
              <div className="hra-alert-msg">{res.message || 'Your leave request has been sent for approval.'}</div>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to submit leave: {err.message}</div>);
    }
  };

  // 3. LEAVE BALANCE
  const handleLeaveBalance = async () => {
    setActiveFlow('leave_balance');
    addMsg('user', 'Leave Balance');
    setLoading(true);
    try {
      const res = await assistantAPI.getLeaveBalance();
      setLoading(false);
      const b = res.balance;
      addMsg('bot', (
        <div>
          <div>Your leave balance:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">🏖️ Casual Leave</span>
              <span className="hra-row-value">{b.casual.remaining} <span style={{ fontSize: 11, color: '#64748b' }}>/ {b.casual.total}</span></span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">🏥 Sick Leave</span>
              <span className="hra-row-value">{b.sick.remaining} <span style={{ fontSize: 11, color: '#64748b' }}>/ {b.sick.total}</span></span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">📋 Earned Leave</span>
              <span className="hra-row-value">{b.earned.remaining} <span style={{ fontSize: 11, color: '#64748b' }}>/ {b.earned.total}</span></span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch leave balance: {err.message}</div>);
    }
  };

  // 4. DOWNLOAD FORM 16
  const handleForm16 = async () => {
    setActiveFlow('form16');
    addMsg('user', 'Download Form 16');
    setLoading(true);
    try {
      const res = await assistantAPI.getForm16Years();
      setLoading(false);
      if (!res.years || res.years.length === 0) {
        addMsg('bot', (
          <div>
            <div>No completed financial year available yet.</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Form 16 can only be issued after a financial year ends (March).
              Once payroll for March is processed, the FY becomes available here.
            </div>
          </div>
        ));
        return;
      }
      addMsg('bot', (
        <div>
          <div>Select financial year:</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
            Only completed FYs are shown. A green tick means the PDF is ready.
          </div>
          <div className="hra-option-grid">
            {res.years.map(y => (
              <button key={y.value} className="hra-option-btn" onClick={() => handleForm16Download(y)}>
                📄 {y.label} {y.available ? '✅' : '⏳'}
              </button>
            ))}
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch years: {err.message}</div>);
    }
  };

  const handleForm16Download = (fy) => {
    addMsg('user', fy.label);
    if (!fy.available || !fy.download_url) {
      addMsg('bot', (
        <div>
          <div>Form 16 for <strong>{fy.label}</strong> has not been generated yet.</div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
            Please ask HR to generate Form 16 from the Forms module. You'll be able to download it here once it's ready.
          </div>
        </div>
      ));
      return;
    }
    window.open(fy.download_url, '_blank');
    addMsg('bot', (
      <div>
        <div>Your Form 16 for <strong>{fy.label}</strong> is opening in a new tab.</div>
        <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
          If the download didn't start,{' '}
          <a href={fy.download_url} target="_blank" rel="noreferrer" style={{ color: '#6366f1', fontWeight: 600 }}>
            click here
          </a>.
        </div>
      </div>
    ));
  };

  // 5. MY PROFILE
  const handleMyProfile = async () => {
    setActiveFlow('my_profile');
    addMsg('user', 'My Profile');
    setLoading(true);
    try {
      const res = await assistantAPI.getProfile();
      setLoading(false);
      if (!res.profile) {
        addMsg('bot', <div>Profile not found. Please contact HR.</div>);
        return;
      }
      const p = res.profile;
      const initials = (p.name || '??').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      addMsg('bot', (
        <div>
          <div className="hra-profile-header">
            <div className="hra-profile-avatar">{initials}</div>
            <div>
              <div className="hra-profile-name">{p.name}</div>
              <div className="hra-profile-role">{p.designation || p.department}</div>
            </div>
          </div>
          <div className="hra-result-card">
            {[
              ['Department', p.department],
              ['Designation', p.designation],
              ['Email', p.email],
              ['Mobile', p.mobile],
              ['Organization', p.organization],
              ['Date of Joining', p.date_of_joining],
              ['Employment Status', p.employment_status],
            ].filter(([, v]) => v && v !== 'None').map(([label, value]) => (
              <div className="hra-result-row" key={label}>
                <span className="hra-row-label">{label}</span>
                <span className="hra-row-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch profile: {err.message}</div>);
    }
  };

  // ── MANAGER FLOWS ──────────────────────────────────────────────────── //

  // 1. APPROVE LEAVE
  const handleApproveLeave = async () => {
    setActiveFlow('approve_leave');
    addMsg('user', 'Approve Leave');
    setLoading(true);
    try {
      const res = await assistantAPI.getPendingLeaves();
      setLoading(false);
      if (!res.requests || res.requests.length === 0) {
        addMsg('bot', (
          <div className="hra-alert hra-alert-success">
            <span className="hra-alert-icon">✅</span>
            <div className="hra-alert-body">
              <div className="hra-alert-title">All Clear</div>
              <div className="hra-alert-msg">No pending leave requests.</div>
            </div>
          </div>
        ));
        return;
      }
      addMsg('bot', (
        <div>
          <div>You have <strong>{res.count}</strong> pending request{res.count > 1 ? 's' : ''}:</div>
          <div style={{ marginTop: 8 }}>
            {res.requests.map(r => (
              <div
                key={r.leave_id}
                className="hra-leave-card"
                onClick={() => handleSelectLeaveRequest(r)}
              >
                <div className="hra-leave-card-name">{r.employee_name}</div>
                <div className="hra-leave-card-detail">
                  {r.leave_type} · {r.start_date} to {r.end_date} ({r.days} day{r.days > 1 ? 's' : ''})
                  {r.reason && <span> · {r.reason}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch leave requests: {err.message}</div>);
    }
  };

  const handleSelectLeaveRequest = (request) => {
    addMsg('user', request.employee_name);
    addMsg('bot', (
      <div>
        <div><strong>{request.employee_name}</strong> — {request.leave_type} leave</div>
        <div style={{ fontSize: 12, color: '#64748b', margin: '4px 0 10px' }}>
          {request.start_date} to {request.end_date} ({request.days} day{request.days > 1 ? 's' : ''})
          {request.reason && <div>Reason: {request.reason}</div>}
        </div>
        <div>Approve or Reject?</div>
        <div className="hra-actions" style={{ marginTop: 8 }}>
          <button className="hra-action-btn hra-action-success" onClick={() => handleLeaveDecision(request.leave_id, 'approve', request.employee_name)}>
            ✅ Approve
          </button>
          <button className="hra-action-btn hra-action-danger" onClick={() => handleLeaveDecision(request.leave_id, 'reject', request.employee_name)}>
            ❌ Reject
          </button>
        </div>
      </div>
    ));
  };

  const handleLeaveDecision = async (leaveId, action, name) => {
    addMsg('user', action === 'approve' ? 'Approve' : 'Reject');
    setLoading(true);
    try {
      await assistantAPI.handleLeaveAction(leaveId, action);
      setLoading(false);
      const label = action === 'approve' ? 'approved' : 'rejected';
      addMsg('bot', (
        <div className={`hra-alert ${action === 'approve' ? 'hra-alert-success' : 'hra-alert-warning'}`}>
          <span className="hra-alert-icon">{action === 'approve' ? '✅' : '❌'}</span>
          <div className="hra-alert-body">
            <div className="hra-alert-title">Request {label}</div>
            <div className="hra-alert-msg">{name}'s leave request has been {label}.</div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to process: {err.message}</div>);
    }
  };

  // 2. TEAM ATTENDANCE
  const handleTeamAttendance = async () => {
    setActiveFlow('team_attendance');
    addMsg('user', 'Team Attendance');
    setLoading(true);
    try {
      const res = await assistantAPI.getTodayAttendance();
      setLoading(false);
      const a = res.attendance;
      addMsg('bot', (
        <div>
          <div>Attendance today ({a.date}):</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">✅ Present</span>
              <span className="hra-row-value">{a.present}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">❌ Absent</span>
              <span className="hra-row-value">{a.absent}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">🏖️ On Leave</span>
              <span className="hra-row-value">{a.on_leave}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">⏳ Not Marked</span>
              <span className="hra-row-value">{a.not_marked}</span>
            </div>
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Total Employees</span>
              <span className="hra-row-value">{a.total_employees}</span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch attendance: {err.message}</div>);
    }
  };

  // 3. ALERTS
  const handleAlerts = async () => {
    setActiveFlow('alerts');
    addMsg('user', 'Alerts');
    setLoading(true);
    try {
      const res = await assistantAPI.getAlerts();
      setLoading(false);
      addMsg('bot', (
        <div>
          {res.alerts.map((a, i) => (
            <div key={i} className={`hra-alert hra-alert-${a.type}`}>
              <span className="hra-alert-icon">
                {a.type === 'warning' ? '⚠️' : a.type === 'error' ? '🔴' : a.type === 'success' ? '✅' : 'ℹ️'}
              </span>
              <div className="hra-alert-body">
                <div className="hra-alert-title">{a.title}</div>
                <div className="hra-alert-msg">{a.message}</div>
              </div>
            </div>
          ))}
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch alerts: {err.message}</div>);
    }
  };

  // ── HR FLOWS ───────────────────────────────────────────────────────── //

  // 1. RUN PAYROLL
  const handleRunPayroll = () => {
    setActiveFlow('run_payroll');
    addMsg('user', 'Run Payroll');
    const now = new Date();
    addMsg('bot', (
      <div>
        <div>Select month to process payroll:</div>
        <div className="hra-month-grid">
          {MONTHS.map((m, i) => (
            <button key={m} className="hra-month-btn" onClick={() => handleRunPayrollMonth(i + 1, now.getFullYear())}>
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>
    ));
  };

  const handleRunPayrollMonth = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      // First get a summary / validation
      const res = await assistantAPI.getPayrollSummary(month, year, selectedOrgRef.current?.id);
      setLoading(false);

      if (res.summary) {
        addMsg('bot', (
          <div>
            <div>Payroll for <strong>{MONTHS[month - 1]} {year}</strong> has already been processed:</div>
            <div className="hra-result-card" style={{ marginTop: 10 }}>
              <div className="hra-result-row">
                <span className="hra-row-label">Employees</span>
                <span className="hra-row-value">{res.summary.employee_count}</span>
              </div>
              <div className="hra-result-row hra-row-highlight">
                <span className="hra-row-label">Total Payout</span>
                <span className="hra-row-value">{fmtRupee(res.summary.total_net_payable)}</span>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
              To re-process, please use the Payroll module from the sidebar.
            </div>
          </div>
        ));
      } else {
        const empRes = await assistantAPI.getEmployeesSummary(selectedOrgRef.current?.id);
        addMsg('bot', (
          <div>
            <div>Payroll ready for <strong>{MONTHS[month - 1]} {year}</strong>:</div>
            <div className="hra-result-card" style={{ marginTop: 10 }}>
              <div className="hra-result-row">
                <span className="hra-row-label">Active Employees</span>
                <span className="hra-row-value">{empRes.summary?.active || 0}</span>
              </div>
            </div>
            <div className="hra-actions" style={{ marginTop: 10 }}>
              <button className="hra-action-btn hra-action-primary" onClick={() => handleRunPayrollConfirm(month, year)}>
                ▶️ Confirm Run
              </button>
              <button className="hra-action-btn" onClick={handleRunPayrollCancel}>
                ❌ Cancel
              </button>
            </div>
          </div>
        ));
      }
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed: {err.message}</div>);
    }
  };

  // 2. PAYROLL SUMMARY
  const handlePayrollSummary = () => {
    setActiveFlow('payroll_summary');
    addMsg('user', 'Payroll Summary');
    const now = new Date();
    const year = now.getFullYear();
    addMsg('bot', (
      <div>
        <div>Which month's payroll summary do you want?</div>
        <div className="hra-month-grid">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              className="hra-month-btn"
              onClick={() => handlePayrollSummaryMonth(i + 1, year)}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          Showing months for {year}
        </div>
      </div>
    ));
  };

  const handlePayrollSummaryMonth = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      const res = await assistantAPI.getPayrollSummary(month, year, selectedOrgRef.current?.id);
      setLoading(false);
      if (!res.summary) {
        addMsg('bot', (
          <div>
            <div>No payroll data for <strong>{MONTHS[month - 1]} {year}</strong>.</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Payroll may not have been processed yet for this month. Try another month.
            </div>
            <div className="hra-actions" style={{ marginTop: 8 }}>
              <button className="hra-action-btn" onClick={handlePayrollSummary}>
                Pick another month
              </button>
            </div>
          </div>
        ));
        return;
      }
      const s = res.summary;
      addMsg('bot', (
        <div>
          <div><strong>{s.month_name} {s.year}</strong> Payroll Summary:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Employees</span>
              <span className="hra-row-value">{s.employee_count}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Gross</span>
              <span className="hra-row-value">{fmtRupee(s.total_gross)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Deductions</span>
              <span className="hra-row-value">{fmtRupee(s.total_deductions)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">PF</span>
              <span className="hra-row-value">{fmtRupee(s.total_pf)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">ESI</span>
              <span className="hra-row-value">{fmtRupee(s.total_esi)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">TDS</span>
              <span className="hra-row-value">{fmtRupee(s.total_tds)}</span>
            </div>
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Net Payable</span>
              <span className="hra-row-value">{fmtRupee(s.total_net_payable)}</span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch summary: {err.message}</div>);
    }
  };

  // 3. EMPLOYEES
  const handleEmployees = async () => {
    setActiveFlow('employees');
    addMsg('user', 'Employees');
    setLoading(true);
    try {
      const res = await assistantAPI.getEmployeesSummary(selectedOrgRef.current?.id);
      setLoading(false);
      const s = res.summary;
      addMsg('bot', (
        <div>
          <div>Employee Overview:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Employees</span>
              <span className="hra-row-value">{s.total}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Active</span>
              <span className="hra-row-value" style={{ color: '#16a34a' }}>{s.active}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Inactive</span>
              <span className="hra-row-value" style={{ color: '#dc2626' }}>{s.inactive}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Departments</span>
              <span className="hra-row-value">{s.departments}</span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch employees: {err.message}</div>);
    }
  };

  // 4. COMPLIANCE
  const handleCompliance = async () => {
    setActiveFlow('compliance');
    addMsg('user', 'Compliance');
    setLoading(true);
    try {
      const res = await assistantAPI.getComplianceDeadlines();
      setLoading(false);
      addMsg('bot', (
        <div>
          <div>Upcoming Compliance Deadlines:</div>
          <div style={{ marginTop: 8 }}>
            {res.deadlines.map((d, i) => (
              <div key={i} className={`hra-alert hra-alert-${d.urgency === 'critical' ? 'error' : d.urgency === 'warning' ? 'warning' : 'info'}`}>
                <span className="hra-alert-icon">
                  {d.urgency === 'critical' ? '🔴' : d.urgency === 'warning' ? '🟡' : '🔵'}
                </span>
                <div className="hra-alert-body">
                  <div className="hra-alert-title">{d.title}</div>
                  <div className="hra-alert-msg">
                    {d.description}
                    {d.due_date && <span> · Due: {d.due_date} ({d.days_left} days left)</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch deadlines: {err.message}</div>);
    }
  };

  // ── CLIENT ADMIN FLOWS ─────────────────────────────────────────────── //

  const renderStatutoryFormsMonthPicker = () => {
    setActiveFlow('statutory_forms');
    const now = new Date();
    const year = now.getFullYear();
    addMsg('user', 'Compliance & Statutory Forms');
    addMsg('bot', (
      <div>
        <div>Which month's statutory forms do you want to view?</div>
        <div className="hra-month-grid">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              className="hra-month-btn"
              onClick={() => pushStep(() => renderStatutoryFormsList(i + 1, year))}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          Showing months for {year}
        </div>
      </div>
    ));
  };

  const handleStatutoryForms = () => {
    stepStackRef.current = [];
    pushStep(renderStatutoryFormsMonthPicker);
  };

  const openFormDownload = (url) => {
    if (!url) return;
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const sep = url.includes('?') ? '&' : '?';
    const full = token ? `${url}${sep}token=${encodeURIComponent(token)}` : url;
    window.open(full, '_blank', 'noopener,noreferrer');
  };

  const renderStatutoryFormsList = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      const res = await assistantAPI.getComplianceForms(month, year, selectedOrgRef.current?.id);
      setLoading(false);
      if (!res.groups || res.groups.length === 0) {
        addMsg('bot', (
          <div>
            <div>No statutory forms found for <strong>{MONTHS[month - 1]} {year}</strong>.</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Forms are generated after payroll is processed. Try another month.
            </div>
            <div className="hra-actions" style={{ marginTop: 8 }}>
              <button className="hra-action-btn" onClick={goBack}>
                Pick another month
              </button>
            </div>
          </div>
        ));
        return;
      }
      addMsg('bot', (
        <div>
          <div>
            Which form do you want for <strong>{res.month_name} {res.year}</strong>?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
            {res.groups.map((g, gi) => {
              const orgFiles = g.files.filter(f => !f.employee_id);
              const empFiles = g.files.filter(f => f.employee_id);
              const isOrg = orgFiles.length > 0;
              const count = isOrg ? orgFiles.length : empFiles.length;
              return (
                <button
                  key={gi}
                  className="hra-menu-btn"
                  style={{ width: '100%' }}
                  onClick={() => pushStep(() => renderStatutoryFormDetail(g, res.month_name, res.year))}
                >
                  <span className="hra-menu-icon hra-icon-amber">📄</span>
                  <span className="hra-menu-text">
                    {g.form_name}
                    <span style={{ display: 'block', fontSize: 11, color: '#64748b', fontWeight: 400 }}>
                      {isOrg
                        ? `Organization form · ${count} file${count === 1 ? '' : 's'}`
                        : `Employee form · ${count} employee${count === 1 ? '' : 's'}`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="hra-actions" style={{ marginTop: 8 }}>
            <button className="hra-action-btn" onClick={goBack}>
              Change month
            </button>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch forms: {err.message}</div>);
    }
  };

  const renderStatutoryFormDetail = (group, monthName, year, page = 0) => {
    addMsg('user', group.form_name);
    const orgFiles = group.files.filter(f => !f.employee_id);
    const empFiles = group.files.filter(f => f.employee_id);

    if (orgFiles.length > 0) {
      addMsg('bot', (
        <div>
          <div>
            <strong>{group.form_name}</strong> — {monthName} {year}
          </div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {orgFiles.map((f, fi) => (
                <button
                  key={fi}
                  className="hra-action-btn"
                  onClick={() => openFormDownload(f.download_url)}
                >
                  ⬇ Download{orgFiles.length > 1 ? ` #${fi + 1}` : ' PDF'}
                </button>
              ))}
            </div>
          </div>
          <div className="hra-actions" style={{ marginTop: 8 }}>
            <button className="hra-action-btn" onClick={goBack}>
              Pick another form
            </button>
          </div>
        </div>
      ));
      return;
    }

    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(empFiles.length / pageSize));
    const start = page * pageSize;
    const pageFiles = empFiles.slice(start, start + pageSize);

    addMsg('bot', (
      <div>
        <div>
          <strong>{group.form_name}</strong> — {monthName} {year}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          Select an employee to download their PDF
          {totalPages > 1 && ` · Page ${page + 1} of ${totalPages}`}
        </div>
        <div className="hra-result-card" style={{ marginTop: 10 }}>
          {pageFiles.map((f, fi) => (
            <div
              key={start + fi}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: fi < pageFiles.length - 1 ? '1px solid #e5e7eb' : 'none',
                fontSize: 13,
              }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }}>
                <div style={{ fontWeight: 500 }}>{f.employee_name || f.employee_id}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{f.employee_id}</div>
              </span>
              <button
                className="hra-action-btn"
                style={{ padding: '4px 10px', fontSize: 11 }}
                onClick={() => openFormDownload(f.download_url)}
              >
                ⬇ PDF
              </button>
            </div>
          ))}
        </div>
        <div className="hra-actions" style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {page > 0 && (
            <button
              className="hra-action-btn"
              onClick={() => {
                setMessages([]);
                renderStatutoryFormDetail(group, monthName, year, page - 1);
              }}
            >
              ← Previous
            </button>
          )}
          {page < totalPages - 1 && (
            <button
              className="hra-action-btn"
              onClick={() => {
                setMessages([]);
                renderStatutoryFormDetail(group, monthName, year, page + 1);
              }}
            >
              Next 10 →
            </button>
          )}
          <button className="hra-action-btn" onClick={goBack}>
            Pick another form
          </button>
        </div>
      </div>
    ));
  };

  const handlePayrollOverview = () => {
    setActiveFlow('payroll_overview');
    addMsg('user', 'Payroll Overview');
    const now = new Date();
    const year = now.getFullYear();
    addMsg('bot', (
      <div>
        <div>Which month's payroll overview do you want?</div>
        <div className="hra-month-grid">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              className="hra-month-btn"
              onClick={() => handlePayrollOverviewMonth(i + 1, year)}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          Showing months for {year}
        </div>
      </div>
    ));
  };

  const handlePayrollOverviewMonth = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      const res = await assistantAPI.getPayrollSummary(month, year, selectedOrgRef.current?.id);
      setLoading(false);
      if (!res.summary) {
        addMsg('bot', (
          <div>
            <div>No payroll data for <strong>{MONTHS[month - 1]} {year}</strong>.</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              Payroll may not have been processed yet for this month. Try another month.
            </div>
            <div className="hra-actions" style={{ marginTop: 8 }}>
              <button className="hra-action-btn" onClick={handlePayrollOverview}>
                Pick another month
              </button>
            </div>
          </div>
        ));
        return;
      }
      const s = res.summary;
      addMsg('bot', (
        <div>
          <div><strong>{s.month_name} {s.year}</strong> Payroll Overview:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Employees</span>
              <span className="hra-row-value">{s.employee_count}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Payout (Gross)</span>
              <span className="hra-row-value">{fmtRupee(s.total_gross)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Deductions</span>
              <span className="hra-row-value">{fmtRupee(s.total_deductions)}</span>
            </div>
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Net Payout</span>
              <span className="hra-row-value">{fmtRupee(s.total_net_payable)}</span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch overview: {err.message}</div>);
    }
  };

  const handleRunPayrollConfirm = (month, year) => {
    addMsg('user', 'Confirm Run');
    addMsg('bot', (
      <div>
        <div>Payroll execution for <strong>{MONTHS[month - 1]} {year}</strong> must be completed from the Payroll module in the sidebar so attendance and statutory validations run correctly.</div>
        <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
          Once processed there, you'll see the final summary here via "Payroll Overview".
        </div>
      </div>
    ));
  };

  const handleRunPayrollCancel = () => {
    addMsg('user', 'Cancel');
    addMsg('bot', <div>Payroll run cancelled. No changes made.</div>);
  };

  const handleReports = () => {
    setActiveFlow('reports');
    addMsg('user', 'Reports & Analytics');
    addMsg('bot', (
      <div>
        <div>What report do you need?</div>
        <div className="hra-menu-grid" style={{ marginTop: 8 }}>
          <button className="hra-menu-btn" onClick={() => handleReportSelect('monthly_payroll')}>
            <span className="hra-menu-icon hra-icon-indigo">📊</span>
            <span className="hra-menu-text">Monthly Payroll Report</span>
          </button>
          <button className="hra-menu-btn" onClick={() => handleReportSelect('department_cost')}>
            <span className="hra-menu-icon hra-icon-emerald">🏢</span>
            <span className="hra-menu-text">Department Cost</span>
          </button>
          <button className="hra-menu-btn" onClick={() => handleReportSelect('employee_salary')}>
            <span className="hra-menu-icon hra-icon-blue">💵</span>
            <span className="hra-menu-text">Employee Salary Report</span>
          </button>
        </div>
      </div>
    ));
  };

  const renderMonthPicker = (label, onPick) => {
    const year = new Date().getFullYear();
    addMsg('bot', (
      <div>
        <div>{label}</div>
        <div className="hra-month-grid">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              className="hra-month-btn"
              onClick={() => onPick(i + 1, year)}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          Showing months for {year}
        </div>
      </div>
    ));
  };

  const handleReportSelect = (reportId) => {
    if (reportId === 'monthly_payroll') {
      addMsg('user', 'Monthly Payroll Report');
      return handlePayrollSummary();
    }
    if (reportId === 'department_cost') {
      addMsg('user', 'Department Cost');
      renderMonthPicker('Which month for the department cost breakdown?', handleDepartmentCostMonth);
      return;
    }
    if (reportId === 'employee_salary') {
      addMsg('user', 'Employee Salary Report');
      renderMonthPicker('Which month for the employee salary report?', handleEmployeeSalaryMonth);
      return;
    }
  };

  const handleDepartmentCostMonth = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      const res = await assistantAPI.getDepartmentCost(month, year, selectedOrgRef.current?.id);
      setLoading(false);
      if (!res.departments || res.departments.length === 0) {
        addMsg('bot', (
          <div>
            <div>No department-level payroll data for <strong>{MONTHS[month - 1]} {year}</strong>.</div>
            <div className="hra-actions" style={{ marginTop: 8 }}>
              <button className="hra-action-btn" onClick={() => handleReportSelect('department_cost')}>
                Pick another month
              </button>
            </div>
          </div>
        ));
        return;
      }
      const total = res.departments.reduce((sum, d) => sum + (d.total_net || 0), 0);
      addMsg('bot', (
        <div>
          <div>Department Cost — <strong>{res.month_name} {res.year}</strong>:</div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            {res.departments.map((d, i) => (
              <div className="hra-result-row" key={i}>
                <span className="hra-row-label">{d.department} ({d.employee_count})</span>
                <span className="hra-row-value">{fmtRupee(d.total_net)}</span>
              </div>
            ))}
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Total Net Payout</span>
              <span className="hra-row-value">{fmtRupee(total)}</span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch department cost: {err.message}</div>);
    }
  };

  const handleEmployeeSalaryMonth = async (month, year) => {
    addMsg('user', `${MONTHS[month - 1]} ${year}`);
    setLoading(true);
    try {
      const res = await assistantAPI.getEmployeeSalaryReport(month, year, selectedOrgRef.current?.id);
      setLoading(false);
      if (!res.employees || res.employees.length === 0) {
        addMsg('bot', (
          <div>
            <div>No employee salary data for <strong>{MONTHS[month - 1]} {year}</strong>.</div>
            <div className="hra-actions" style={{ marginTop: 8 }}>
              <button className="hra-action-btn" onClick={() => handleReportSelect('employee_salary')}>
                Pick another month
              </button>
            </div>
          </div>
        ));
        return;
      }
      const t = res.totals || {};
      addMsg('bot', (
        <div>
          <div>Employee Salary Report — <strong>{res.month_name} {res.year}</strong>:</div>
          <div className="hra-result-card" style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }}>
            {res.employees.map((e, i) => (
              <div className="hra-result-row" key={i} style={{ alignItems: 'flex-start' }}>
                <span className="hra-row-label" style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{e.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {e.employee_id} • {e.department}
                  </div>
                </span>
                <span className="hra-row-value" style={{ textAlign: 'right' }}>
                  <div>{fmtRupee(e.net)}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    Gross {fmtRupee(e.gross)}
                  </div>
                </span>
              </div>
            ))}
          </div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            <div className="hra-result-row">
              <span className="hra-row-label">Employees</span>
              <span className="hra-row-value">{t.employee_count}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Gross</span>
              <span className="hra-row-value">{fmtRupee(t.total_gross)}</span>
            </div>
            <div className="hra-result-row">
              <span className="hra-row-label">Total Deductions</span>
              <span className="hra-row-value">{fmtRupee(t.total_deductions)}</span>
            </div>
            <div className="hra-result-row hra-row-highlight">
              <span className="hra-row-label">Net Payout</span>
              <span className="hra-row-value">{fmtRupee(t.total_net)}</span>
            </div>
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to fetch salary report: {err.message}</div>);
    }
  };

  const handleOrgSettings = async () => {
    setActiveFlow('org_settings');
    addMsg('user', 'Organization Settings');
    setLoading(true);
    try {
      const res = await assistantAPI.getOrganizationSettings(selectedOrgRef.current?.id);
      setLoading(false);
      if (!res.success || !res.settings) {
        addMsg('bot', <div>{res.message || 'Could not load organization settings.'}</div>);
        return;
      }
      const s = res.settings;
      const row = (label, value) => (
        <div className="hra-result-row">
          <span className="hra-row-label">{label}</span>
          <span className="hra-row-value">{value || '—'}</span>
        </div>
      );
      addMsg('bot', (
        <div>
          <div><strong>{s.name}</strong></div>
          <div className="hra-result-card" style={{ marginTop: 10 }}>
            {row('Code', s.code)}
            {row('Address', [s.address, s.city, s.state, s.zip_code].filter(Boolean).join(', '))}
            {row('Phone', s.phone)}
            {row('Email', s.email)}
            {row('Employer', [s.employer_name, s.employer_designation].filter(Boolean).join(' · '))}
            {row('Manager', s.manager_name)}
            {row('Contract', [s.contract_start_date, s.contract_valid_till].filter(Boolean).join(' → '))}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            To update company details, payroll configuration, or salary structures, please use the <strong>Organization</strong> module from the sidebar.
          </div>
        </div>
      ));
    } catch (err) {
      setLoading(false);
      addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to load settings: {err.message}</div>);
    }
  };

  /* ── Org picker (SuperAdmin) ────────────────────────────────────────── */

  const renderOrgPicker = (menuLabel, onPick) => {
    setActiveFlow('org_picker');
    addMsg('user', menuLabel);
    setLoading(true);
    (async () => {
      try {
        let orgs = orgsCacheRef.current;
        if (!orgs) {
          const res = await assistantAPI.getOrganizations();
          orgs = res.organizations || [];
          orgsCacheRef.current = orgs;
        }
        setLoading(false);
        if (orgs.length === 0) {
          addMsg('bot', <div>No organizations available.</div>);
          return;
        }
        addMsg('bot', (
          <div>
            <div>Which organization?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {orgs.map((o) => (
                <button
                  key={o.id}
                  className="hra-menu-btn"
                  style={{ width: '100%' }}
                  onClick={() => {
                    selectedOrgRef.current = o;
                    pushStep(() => onPick(o));
                  }}
                >
                  <span className="hra-menu-icon hra-icon-indigo">🏢</span>
                  <span className="hra-menu-text">
                    {o.name}
                    {o.code && (
                      <span style={{ display: 'block', fontSize: 11, color: '#64748b', fontWeight: 400 }}>
                        {o.code}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ));
      } catch (err) {
        setLoading(false);
        addMsg('bot', <div style={{ color: '#dc2626' }}>Failed to load organizations: {err.message}</div>);
      }
    })();
  };

  const withOrgScope = (menuLabel, runner) => {
    if (!isSuperAdmin) {
      runner();
      return;
    }
    stepStackRef.current = [];
    pushStep(() => renderOrgPicker(menuLabel, () => runner()));
  };

  /* ── Flow dispatch ──────────────────────────────────────────────────── */

  const handleMenuClick = (menuId) => {
    switch (menuId) {
      case 'view_payslip':     return handleViewPayslip();
      case 'apply_leave':      return handleApplyLeave();
      case 'leave_balance':    return handleLeaveBalance();
      case 'form16':           return handleForm16();
      case 'my_profile':       return handleMyProfile();
      case 'approve_leave':    return handleApproveLeave();
      case 'team_attendance':  return handleTeamAttendance();
      case 'alerts':           return handleAlerts();
      case 'run_payroll':      return handleRunPayroll();
      case 'payroll_summary':  return handlePayrollSummary();
      case 'payroll_overview': return withOrgScope('Payroll Overview', handlePayrollOverview);
      case 'reports':          return withOrgScope('Reports & Analytics', handleReports);
      case 'employees':        return withOrgScope('Employee Summary', handleEmployees);
      case 'compliance':       return isSuperAdmin
                                 ? withOrgScope('Compliance & Statutory Forms', handleStatutoryForms)
                                 : isClientAdmin ? handleStatutoryForms() : handleCompliance();
      case 'org_settings':     return withOrgScope('Organization Settings', handleOrgSettings);
      default: break;
    }
  };

  /* ── Intent router (free-text queries) ─────────────────────────────── */

  const resolveIntent = (text) => {
    const t = text.toLowerCase();
    const has = (...words) => words.some(w => t.includes(w));

    if (!isClientAdmin && has('payslip', 'pay slip', 'salary slip', 'my salary', 'net pay', 'take home')) return 'view_payslip';
    if (!isClientAdmin && has('apply leave', 'book leave', 'request leave', 'take leave', 'leave request')) return 'apply_leave';
    if (!isClientAdmin && has('leave balance', 'leaves left', 'leaves remaining', 'how many leaves', 'holidays left')) return 'leave_balance';
    if (!isClientAdmin && has('form 16', 'form16', 'tax form', 'itr form')) return 'form16';
    if (!isClientAdmin && has('my profile', 'my details', 'my info', 'profile')) return 'my_profile';

    if (has('payroll overview', 'overview', 'this month payroll', 'current payroll')) return 'payroll_overview';
    if (has('run payroll', 'process payroll', 'execute payroll', 'start payroll')) return 'run_payroll';
    if (has('report', 'analytics', 'trend', 'why payroll increased', 'department cost', 'salary report')) return 'reports';
    if (has('compliance', 'filing', 'deadline', 'due date', 'pf due', 'esi due', 'tds due', 'form 24q')) return 'compliance';
    if (has('employee summary', 'headcount', 'how many employees', 'total employees', 'employee list', 'staff list')) return 'employees';
    if (has('org settings', 'organization settings', 'company details', 'settings', 'salary structure', 'payroll config')) return 'org_settings';

    if (has('approve leave', 'pending leave', 'leave approval')) return 'approve_leave';
    if (has('team attendance', 'who is present', "who's present", 'attendance today', 'team today')) return 'team_attendance';
    if (has('alert', 'notification', 'reminder')) return 'alerts';
    if (has('payroll summary', 'salary summary', 'monthly payroll')) return isClientAdmin ? 'payroll_overview' : 'payroll_summary';
    if (has('leave')) return isClientAdmin ? 'compliance' : (isManager ? 'approve_leave' : 'leave_balance');
    if (has('payroll')) return isClientAdmin ? 'payroll_overview' : (isHR ? 'payroll_summary' : 'view_payslip');
    return null;
  };

  const runQuery = async (text) => {
    if (!text || loading) return;
    const intent = resolveIntent(text);
    if (intent) {
      addMsg('user', text);
      handleMenuClick(intent);
      return;
    }
    // Fall through to LLM-backed AI chat for unknown queries
    addMsg('user', text);
    setLoading(true);
    try {
      const context = {
        user_id: user?.id,
        employee_id: user?.employee_id,
        establishment_id: selectedOrgRef.current?.id || user?.establishment_id,
      };
      const res = await assistantAPI.askAI(text, context, []);
      setLoading(false);
      if (res && res.answer) {
        addMsg('bot', (
          <div>
            <div>{renderAnswerWithTables(res.answer)}</div>
            {res.sources && res.sources.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Sources:</div>
                {res.sources.slice(0, 3).map((s, i) => (
                  <div key={i}>· {s.file_name || s.doc_id || 'document'}</div>
                ))}
              </div>
            )}
            {res.agent_type && (
              <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
                via {res.agent_type}
              </div>
            )}
          </div>
        ));
      } else {
        addMsg('bot', <div>Sorry, I couldn't find an answer for that.</div>);
      }
      return;
    } catch (err) {
      setLoading(false);
      addMsg('bot', (
        <div>
          <div style={{ color: '#dc2626' }}>AI lookup failed: {err.message}</div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
            Try one of the menu items above instead.
          </div>
        </div>
      ));
    }
  };

  const handleQuerySubmit = async (e) => {
    if (e) e.preventDefault();
    const text = queryText.trim();
    if (!text || loading) return;
    setQueryText('');
    await runQuery(text);
  };

  // Clickable suggestions — these strings are all shapes the backend fast-path
  // handles in ~10ms (no LLM call), so clicking them feels instant.
  const suggestedQueries = isEmployee
    ? ['My payslip', 'My leave balance', 'Apply leave']
    : ['Show me employees', 'List organizations', 'How many employees'];

  /* ── Render ─────────────────────────────────────────────────────────── */

  const menu = getMenu();
  const roleLabel = isEmployee ? 'Employee' : isSuperAdmin ? 'Super Admin' : isClientAdmin ? 'Client Admin' : 'Manager';

  return (
    <div className="hra-container">
      {/* Nav bar (only when in a flow) */}
      {activeFlow && (
        <div className="hra-nav">
          <button className="hra-nav-btn" onClick={goBack}>
            <ArrowLeft size={14} /> Back
          </button>
          <button className="hra-nav-btn" onClick={goHome}>
            <Home size={14} /> Home
          </button>
          {isSuperAdmin && selectedOrgRef.current && activeFlow !== 'org_picker' && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569', background: '#eef2ff', padding: '4px 10px', borderRadius: 12 }}>
              🏢 {selectedOrgRef.current.name}
            </span>
          )}
        </div>
      )}

      <div className="hra-scroll" ref={scrollRef}>
        {/* Welcome + menu (shown when no flow active) */}
        {!activeFlow && (
          <div className="hra-bubble hra-bubble-bot">
            <div className="hra-bubble-label">
              <Sparkles size={12} /> HR Assistant
            </div>
            <div>Hi <strong>{userName}</strong>! How can I help you today?</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              Logged in as <strong>{roleLabel}</strong>
            </div>
            <div className="hra-menu-grid">
              {menu.map(item => (
                <button
                  key={item.id}
                  className="hra-menu-btn"
                  onClick={() => handleMenuClick(item.id)}
                >
                  <span className={`hra-menu-icon ${item.color}`}>{item.icon}</span>
                  <span className="hra-menu-text">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat messages */}
        {messages.map(msg => (
          <div key={msg.id} className={`hra-bubble hra-bubble-${msg.type}`}>
            {msg.type === 'bot' && (
              <div className="hra-bubble-label">
                <Sparkles size={12} /> HR Assistant
              </div>
            )}
            {typeof msg.content === 'string' ? <div>{msg.content}</div> : msg.content}
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div className="hra-loading">
            <div className="hra-dot-loader">
              <span /><span /><span />
            </div>
            <span className="hra-loading-text">Fetching data…</span>
          </div>
        )}
      </div>

      {!loading && suggestedQueries.length > 0 && (
        <div className="hra-suggestions" aria-label="Suggested queries">
          {suggestedQueries.map(s => (
            <button
              key={s}
              type="button"
              className="hra-chip"
              onClick={() => runQuery(s)}
              title={s}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form className="hra-composer" onSubmit={handleQuerySubmit}>
        <input
          type="text"
          className="hra-composer-input"
          placeholder="Ask me anything… e.g. 'Show my payslip' or 'Leave balance'"
          value={queryText}
          onChange={e => setQueryText(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="hra-composer-send"
          disabled={loading || !queryText.trim()}
        >
          Send
        </button>
      </form>
    </div>
  );
}


/* ── Sub-components (date picker, reason input) ────────────────────────── */

function LeaveeDatePicker({ onSubmit }) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const today = new Date().toISOString().split('T')[0];

  return (
    <div>
      <div>Select dates:</div>
      <div className="hra-date-group">
        <div className="hra-date-row">
          <label>From</label>
          <input type="date" value={start} min={today} onChange={e => { setStart(e.target.value); if (!end || e.target.value > end) setEnd(e.target.value); }} />
        </div>
        <div className="hra-date-row">
          <label>To</label>
          <input type="date" value={end} min={start || today} onChange={e => setEnd(e.target.value)} />
        </div>
        <button
          className="hra-action-btn hra-action-primary"
          disabled={!start || !end}
          onClick={() => onSubmit(start, end)}
          style={{ alignSelf: 'flex-start', marginTop: 4 }}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function LeaveReasonInput({ onSubmit }) {
  const [reason, setReason] = useState('');

  return (
    <div>
      <div>Enter reason (optional):</div>
      <textarea
        className="hra-textarea"
        placeholder="e.g. Family function, personal work..."
        value={reason}
        onChange={e => setReason(e.target.value)}
      />
      <div className="hra-actions" style={{ marginTop: 6 }}>
        <button className="hra-action-btn hra-action-primary" onClick={() => onSubmit(reason)}>
          Continue →
        </button>
        <button className="hra-action-btn" onClick={() => onSubmit('')}>
          Skip
        </button>
      </div>
    </div>
  );
}
